import { useState } from 'react';
import { Form, Input, InputNumber, Select, Button, Card, Row, Col, Divider, List, Typography, Spin, Tag, Tooltip } from 'antd';
import { InfoCircleOutlined, CalculatorOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import axios from '../../config/axios';
import { handleAPIError } from '../../utils/helpers';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

interface PricingFormValues {
	phone_model: string;
	phone_amount: number;
	duration: number;
	duration_unit: string;
	payment_plan: string;
	interest: number;
	vendor_id: string;
	cash_in_hand?: number;
	funding_type: 'retailer_funded' | 'zazu_funded';
}

interface PricingResponse {
	duration: number;
	duration_unit: string;
	payment_plan: string;
	phone_amount: number;
	interest_percentage: number;
	interest_amount: number;
	total_amount_to_be_paid: number;
	down_payment: number;
	paid_down_payment_amount: number;
	due_down_payment_amount: number;
	cash_in_hand: number;
	number_of_installments: number;
	number_of_regular_installments: number;
	total_installment_amount: number;
	per_installment_amount: number;
	installment_payment_dates: string[];
	cash_with_retailer: number;
	meets_minimum_down_payment: boolean;
	down_payment_shortfall: number;
}

const Calculator = () => {
	const [form] = Form.useForm<PricingFormValues>();
	const [loading, setLoading] = useState(false);
	const [result, setResult] = useState<PricingResponse | null>(null);
	const [fundingType, setFundingType] = useState<'retailer_funded' | 'zazu_funded'>('retailer_funded');
	const navigate = useNavigate();

	const fundingTypeOptions = [
		{ label: 'Retailer Funded', value: 'retailer_funded' },
		{ label: 'Zazu Funded', value: 'zazu_funded' },
	];

	const durationOptions = [
		{ label: '1', value: 1 },
		{ label: '3', value: 3 },
		{ label: '6', value: 6 },
		{ label: '12', value: 12 },
	];

	const durationUnitOptions = [
		{ label: 'Week', value: 'week' },
		{ label: 'Month', value: 'month' },
	];

	const paymentPlanOptions = [
		{ label: 'Daily', value: 'daily' },
		{ label: 'Weekly', value: 'weekly' },
		{ label: 'Monthly', value: 'monthly' },
	];

	const handleSubmit = async (values: PricingFormValues) => {
		try {
			setLoading(true);
			// If zazu_funded is selected, completely remove interest and vendor_id fields
			if (values.funding_type === 'zazu_funded') {
				// Create a new object manually without the unwanted properties
				const filteredValues = {
					phone_model: values.phone_model,
					phone_amount: values.phone_amount,
					duration: values.duration,
					duration_unit: values.duration_unit,
					payment_plan: values.payment_plan,
					funding_type: values.funding_type,
					...(values.cash_in_hand !== undefined && { cash_in_hand: values.cash_in_hand }),
				};
				const response = await axios.post('/devices/pricing-breakdown', filteredValues);
				setResult(response.data.results);
			} else {
				const response = await axios.post('/devices/pricing-breakdown', values);
				setResult(response.data.results);
			}
		} catch (error: unknown) {
			handleAPIError(error as Error);
		} finally {
			setLoading(false);
		}
	};

	const handleFundingTypeChange = (value: 'retailer_funded' | 'zazu_funded') => {
		setFundingType(value);
		if (value === 'zazu_funded') {
			form.setFieldsValue({ interest: 0, vendor_id: '' });
		}
	};

	const formatCurrency = (amount: number) => {
		return `UGX ${amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
	};

	return (
		<div className="mx-auto max-w-4xl px-4 py-6 md:py-10">
			<Card
				className="overflow-hidden rounded-xl shadow-md"
				title={
					<div className="flex w-full items-center justify-between">
						<div className="flex items-center gap-2">
							<CalculatorOutlined className="text-xl text-primary" />
							<span className="text-xl font-bold">Device Loan Calculator</span>
						</div>
						<Button
							icon={<ArrowLeftOutlined />}
							onClick={() => navigate(-1)}
							type="default"
							className="flex items-center"
						>
							Back
						</Button>
					</div>
				}
			>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleSubmit}
					requiredMark={false}
					initialValues={{
						duration: 3,
						duration_unit: 'month',
						payment_plan: 'weekly',
						interest: 10.0,
						funding_type: 'retailer_funded',
					}}
				>
					<Row gutter={[16, 16]}>
						<Col
							xs={24}
							md={12}
						>
							<Form.Item
								name="funding_type"
								label="Funding Type"
								rules={[{ required: true, message: 'Please select the funding type' }]}
							>
								<Select
									className="h-11"
									options={fundingTypeOptions}
									placeholder="Select funding type"
									onChange={handleFundingTypeChange}
								/>
							</Form.Item>
						</Col>

						<Col
							xs={24}
							md={12}
						>
							<Form.Item
								name="phone_model"
								label="Phone Model"
								rules={[{ required: true, message: 'Please enter the phone model' }]}
							>
								<Input
									placeholder="e.g. Tecno Spark 2"
									maxLength={100}
									className="h-11"
								/>
							</Form.Item>
						</Col>

						<Col
							xs={24}
							md={12}
						>
							<Form.Item
								name="phone_amount"
								label="Phone Amount"
								rules={[{ required: true, message: 'Please enter the phone amount' }]}
							>
								<InputNumber
									className="h-11 w-full"
									placeholder="e.g. 1000000"
									formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
									parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
									addonBefore="UGX"
								/>
							</Form.Item>
						</Col>

						<Col
							xs={24}
							md={8}
						>
							<Form.Item
								name="duration"
								label="Duration"
								rules={[{ required: true, message: 'Please select the duration' }]}
							>
								<Select
									className="h-11"
									options={durationOptions}
									placeholder="Select duration"
								/>
							</Form.Item>
						</Col>

						<Col
							xs={24}
							md={8}
						>
							<Form.Item
								name="duration_unit"
								label="Duration Unit"
								rules={[{ required: true, message: 'Please select the duration unit' }]}
							>
								<Select
									className="h-11"
									options={durationUnitOptions}
									placeholder="Select duration unit"
								/>
							</Form.Item>
						</Col>

						<Col
							xs={24}
							md={8}
						>
							<Form.Item
								name="payment_plan"
								label="Payment Plan"
								rules={[{ required: true, message: 'Please select the payment plan' }]}
							>
								<Select
									className="h-11"
									options={paymentPlanOptions}
									placeholder="Select payment plan"
								/>
							</Form.Item>
						</Col>

						{fundingType === 'retailer_funded' && (
							<>
								<Col
									xs={24}
									md={8}
								>
									<Form.Item
										name="interest"
										label={
											<span>
												Interest (%)
												<Tooltip title="Interest percentage for the loan">
													<InfoCircleOutlined className="ml-1" />
												</Tooltip>
											</span>
										}
										rules={[{ required: true, message: 'Please enter the interest rate' }]}
									>
										<InputNumber
											className="h-11 w-full"
											placeholder="e.g. 10.00"
											min={0}
											max={100}
											step={0.01}
											precision={2}
										/>
									</Form.Item>
								</Col>

								<Col
									xs={24}
									md={8}
								>
									<Form.Item
										name="vendor_id"
										label="Vendor ID"
										rules={[{ required: true, message: 'Please enter the vendor ID' }]}
									>
										<Input
											placeholder="e.g. RTABZ6WJXK"
											className="h-11"
										/>
									</Form.Item>
								</Col>
							</>
						)}

						<Col
							xs={24}
							md={fundingType === 'retailer_funded' ? 8 : 16}
						>
							<Form.Item
								name="cash_in_hand"
								label={
									<span>
										Cash In Hand (Optional)
										{/* <Tooltip title="Amount customer has available for down payment">
											<InfoCircleOutlined className="ml-1" />
										</Tooltip> */}
									</span>
								}
							>
								<InputNumber
									className="h-11 w-full"
									placeholder="e.g. 500000"
									formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
									parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
									addonBefore="UGX"
								/>
							</Form.Item>
						</Col>

						<Col xs={24}>
							<Form.Item>
								<Button
									type="primary"
									htmlType="submit"
									loading={loading}
									className="h-11 w-full font-bold md:w-auto"
									size="large"
								>
									Calculate Payment Plan
								</Button>
							</Form.Item>
						</Col>
					</Row>
				</Form>

				{loading && (
					<div className="flex justify-center py-8">
						<Spin size="large" />
					</div>
				)}

				{result && !loading && (
					<div className="mt-6">
						<Divider className="my-6">
							<Title
								level={4}
								className="m-0"
							>
								Payment Details
							</Title>
						</Divider>

						<Row gutter={[16, 16]}>
							<Col
								xs={24}
								md={12}
							>
								<Card className="h-full border-0 bg-gray-50 dark:bg-dark-bg">
									<Title level={5}>Loan Summary</Title>
									<List
										className="mt-4"
										itemLayout="horizontal"
										dataSource={[
											{
												label: 'Device Price',
												value: formatCurrency(result.phone_amount),
											},
											{
												label: 'Interest Rate',
												value: `${result.interest_percentage}%`,
											},
											{
												label: 'Interest Amount',
												value: formatCurrency(result.interest_amount),
											},
											{
												label: 'Duration',
												value: `${result.duration} ${result.duration_unit}${result.duration > 1 ? 's' : ''}`,
											},
											{
												label: 'Payment Frequency',
												value: result.payment_plan.charAt(0).toUpperCase() + result.payment_plan.slice(1),
											},
											{
												label: 'Total Amount to Pay',
												value: formatCurrency(result.total_amount_to_be_paid),
												highlight: true,
											},
										]}
										renderItem={(item) => (
											<List.Item className="px-0 py-2">
												<div className="flex w-full justify-between">
													<Text>{item.label}</Text>
													<Text
														strong={item.highlight}
														className={item.highlight ? 'text-lg text-primary' : ''}
													>
														{item.value}
													</Text>
												</div>
											</List.Item>
										)}
									/>
								</Card>
							</Col>

							<Col
								xs={24}
								md={12}
							>
								<Card className="h-full border-0 bg-gray-50 dark:bg-dark-bg">
									<Title level={5}>Payment Breakdown</Title>
									<List
										className="mt-4"
										itemLayout="horizontal"
										dataSource={[
											{
												label: 'Minimum Down Payment',
												value: formatCurrency(result.down_payment),
											},
											{
												label: 'Cash in Hand',
												value:
													result.cash_in_hand > 0 && !result.meets_minimum_down_payment ? (
														<span>
															{formatCurrency(result.cash_in_hand)}{' '}
															<Tag color="error">below minimum</Tag>
														</span>
													) : (
														formatCurrency(result.cash_in_hand)
													),
											},
											{
												label: 'Due Down Payment',
												value: formatCurrency(result.due_down_payment_amount),
												highlight: result.due_down_payment_amount > 0,
											},
											{
												label: 'Cash with Retailer',
												value: formatCurrency(result.cash_with_retailer),
											},
											{
												label: 'Number of Installments',
												value: result.number_of_installments - 1,
											},
											{
												label: 'Per Installment Amount',
												value: formatCurrency(result.per_installment_amount),
												highlight: true,
											},
										]}
										renderItem={(item) => (
											<List.Item className="px-0 py-2">
												<div className="flex w-full justify-between">
													<Text>{item.label}</Text>
													<Text
														strong={item.highlight}
														className={item.highlight ? 'text-lg text-primary' : ''}
													>
														{item.value}
													</Text>
												</div>
											</List.Item>
										)}
									/>
								</Card>
							</Col>

							<Col xs={24}>
								<Card className="border-0 bg-gray-50 dark:bg-dark-bg">
									<Title level={5}>Payment Schedule</Title>
									<div className="overflow-x-auto">
										<table className="mt-4 min-w-full">
											<thead>
												<tr className="border-b">
													<th className="px-4 py-3 text-left">Payment #</th>
													<th className="px-4 py-3 text-left">Date</th>
													<th className="px-4 py-3 text-right">Amount</th>
												</tr>
											</thead>
											<tbody>
												{result.installment_payment_dates.slice(1).map((date, index) => (
													<tr
														key={index}
														className="border-b"
													>
														<td className="px-4 py-3">Payment {index + 1}</td>
														<td className="px-4 py-3">{dayjs(date).format('DD MMM YYYY')}</td>
														<td className="px-4 py-3 text-right">{formatCurrency(result.per_installment_amount)}</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</Card>
							</Col>
						</Row>
					</div>
				)}
			</Card>
		</div>
	);
};

export default Calculator;
